import { rem } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { MAX_POST_PHOTOS, PHOTO_CAPTION_MAX, photoUrl, type PostPhoto } from '@reditelji/shared';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isTransient } from '../api/client';
import { usePhotoMutations } from '../api/photos';
import { useSession } from '../api/session';
import { PhotoLightbox } from '../components/PhotoLightbox';
import type { PostRow } from '../api/posts';
import { PhotoError, preparePhoto } from './photoPipeline';

/** A file being prepared and uploaded, shown in place until the real row lands. */
interface PendingPhoto {
  localId: string;
  name: string;
  previewUrl: string;
}

/**
 * Photos of a post (SPEC §1.1D), the way a marshal sees the place before they
 * get there. The first one is the cover: it leads the briefing filmstrip and
 * labels the post on the map.
 *
 * These write immediately, unlike every other field in the editor. A photo is
 * not something you can leave half-typed, and bytes held in component state
 * until someone presses Shrani are bytes lost to a stray reload — so the card
 * says so in as many words rather than leaving it to be discovered.
 */
export function PostPhotos({ post }: { post: PostRow }) {
  const mut = usePhotoMutations(post.id);
  // Photos are part of the post: the organisation looks at them, it does not
  // upload, reorder, caption or delete (SPEC §1.1L).
  const canEdit = useSession().can('plan:write');
  const [pending, setPending] = useState<PendingPhoto[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  /** Photo being looked at full size, by position in the strip. */
  const [preview, setPreview] = useState<number | null>(null);
  /** Order shown while the reorder request is in flight. */
  const [order, setOrder] = useState<string[] | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const photos = order
    ? [...post.photos].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    : post.photos;
  const room = MAX_POST_PHOTOS - photos.length - pending.length;

  useFlip(listRef, photos.map((p) => p.id).join());

  // Every preview is revoked when its upload settles; this catches the ones
  // still in flight if the coordinator selects another post mid-upload.
  const live = useRef(pending);
  live.current = pending;
  useEffect(
    () => () => {
      for (const p of live.current) URL.revokeObjectURL(p.previewUrl);
    },
    [],
  );

  const addFiles = async (files: File[]) => {
    if (files.length > room) {
      notifications.show({
        color: 'yellow',
        title: 'Preveč fotografij',
        message: `Mesto sprejme ${MAX_POST_PHOTOS}. Dodajam prvih ${Math.max(0, room)}.`,
      });
    }
    // One at a time: encoding a 12 MP photo is the slow part, and a parallel
    // burst would stall the tab that is showing the progress.
    for (const file of files.slice(0, Math.max(0, room))) {
      const localId = crypto.randomUUID();
      const previewUrl = URL.createObjectURL(file);
      setPending((list) => [...list, { localId, name: file.name, previewUrl }]);
      try {
        let prepared;
        try {
          prepared = await preparePhoto(file);
        } catch (err) {
          // Preparation happens before any mutation, so nothing else will
          // report this. A row that just disappears is the one outcome the
          // coordinator cannot act on — say what went wrong even when the
          // cause is something we did not anticipate.
          notifications.show({
            color: 'red',
            title: 'Fotografije ni bilo mogoče dodati',
            message:
              err instanceof PhotoError
                ? err.message
                : `»${file.name}« se ni dala pripraviti za nalaganje. Poskusi z drugo datoteko.`,
          });
          continue;
        }
        // The upload's own failures already surface through the mutation.
        await mut.upload.mutateAsync(prepared).catch(() => undefined);
      } finally {
        URL.revokeObjectURL(previewUrl);
        setPending((list) => list.filter((p) => p.localId !== localId));
      }
    }
  };

  const move = (id: string, by: -1 | 1) => {
    const ids = photos.map((p) => p.id);
    const from = ids.indexOf(id);
    const to = from + by;
    if (to < 0 || to >= ids.length) return;
    [ids[from], ids[to]] = [ids[to]!, ids[from]!];
    setOrder(ids);
    // Cleared only once the refetch has landed — dropping it as soon as the
    // request returns would show the old order again for a round trip, and the
    // rows would animate there and back.
    mut.reorder.mutateAsync(ids).catch(() => undefined).finally(() => setOrder(null));
  };

  return (
    <div
      className={`pp-photos${dragOver ? ' pp-photos--over' : ''}`}
      onDragOver={(e) => {
        if (!canEdit) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        if (!canEdit) return;
        e.preventDefault();
        setDragOver(false);
        const files = [...e.dataTransfer.files].filter((f) => f.type.startsWith('image/'));
        if (files.length) void addFiles(files);
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
        <span style={{ fontSize: rem(12), letterSpacing: '0.04em', color: 'oklch(0.55 0.012 90)' }}>
          FOTOGRAFIJE MESTA
        </span>
        <div style={{ flex: 1 }} />
        <span
          className="pp-mono"
          style={{ fontSize: rem(11.5), color: room > 0 ? 'oklch(0.62 0.012 90)' : 'oklch(0.55 0.1 75)' }}
        >
          {photos.length}/{MAX_POST_PHOTOS}
        </span>
      </div>
      {canEdit ? (
        <div style={{ fontSize: rem(12), color: 'oklch(0.6 0.012 90)', lineHeight: 1.4, marginBottom: 10 }}>
          Shranijo se takoj, brez gumba Shrani. Prva je naslovna — redar jo vidi prvo.
        </div>
      ) : photos.length === 0 ? (
        <div style={{ fontSize: rem(12.5), color: 'oklch(0.62 0.012 90)' }}>Brez fotografij.</div>
      ) : null}

      <div ref={listRef} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {photos.map((photo, i) => (
          <PhotoRow
            key={photo.id}
            photo={photo}
            postCode={post.code}
            cover={i === 0}
            first={i === 0}
            last={i === photos.length - 1}
            confirming={confirmId === photo.id}
            readOnly={!canEdit}
            onPreview={() => setPreview(i)}
            onMove={(by) => move(photo.id, by)}
            onAskDelete={() => setConfirmId(photo.id)}
            onCancelDelete={() => setConfirmId(null)}
            onDelete={() => {
              setConfirmId(null);
              mut.remove.mutate(photo.id);
            }}
            onCaption={(patch) => mut.caption.mutateAsync({ id: photo.id, ...patch })}
          />
        ))}

        {pending.map((p) => (
          <div key={p.localId} className="pp-photo pp-photo--pending">
            <img className="pp-photo-thumb" src={p.previewUrl} alt="" />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: rem(13), color: 'oklch(0.45 0.012 90)' }}>{p.name}</div>
              <div style={{ fontSize: rem(12), color: 'oklch(0.62 0.012 90)', marginTop: 2 }}>
                pripravljam in nalagam…
              </div>
              <div className="pp-photo-bar" />
            </div>
          </div>
        ))}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          const files = [...(e.currentTarget.files ?? [])];
          e.currentTarget.value = '';
          if (files.length) void addFiles(files);
        }}
      />

      {preview === null ? null : (
        <PhotoLightbox
          photos={photos.map((p) => ({
            key: p.key,
            caption: p.captionSl,
            alt: p.captionSl || `Fotografija mesta ${post.code}`,
          }))}
          index={preview}
          labels={{
            close: 'Zapri',
            prev: 'Prejšnja fotografija',
            next: 'Naslednja fotografija',
            counter: '{n} od {m}',
          }}
          onIndex={setPreview}
          onClose={() => setPreview(null)}
        />
      )}

      {!canEdit ? null : room > 0 ? (
        <button
          className="pp-photo-add"
          style={{ marginTop: photos.length || pending.length ? 8 : 0 }}
          onClick={() => fileRef.current?.click()}
        >
          <span style={{ fontSize: rem(13.5), fontWeight: 600, color: 'oklch(0.4 0.08 245)' }}>
            + Dodaj fotografijo
          </span>
          <span style={{ fontSize: rem(11.5), color: 'oklch(0.55 0.03 245)' }}>
            {photos.length || pending.length
              ? 'ali spusti datoteke sem'
              : 'posnetek pove, kje točno naj redar stoji — ali spusti datoteke sem'}
          </span>
        </button>
      ) : (
        <div style={{ marginTop: 8, fontSize: rem(12), color: 'oklch(0.55 0.1 75)', lineHeight: 1.4 }}>
          Mesto ima največje dovoljeno število fotografij. Izbriši eno, da dodaš novo.
        </div>
      )}
    </div>
  );
}

function PhotoRow({
  photo,
  postCode,
  cover,
  first,
  last,
  confirming,
  readOnly,
  onPreview,
  onMove,
  onAskDelete,
  onCancelDelete,
  onDelete,
  onCaption,
}: {
  photo: PostPhoto;
  postCode: string;
  cover: boolean;
  first: boolean;
  last: boolean;
  confirming: boolean;
  /** Captions as text, no reorder or delete: a caption field saves on blur. */
  readOnly: boolean;
  onPreview: () => void;
  onMove: (by: -1 | 1) => void;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onDelete: () => void;
  /** Resolves once the server holds the caption. */
  onCaption: (patch: { captionSl?: string; captionEn?: string }) => Promise<unknown>;
}) {
  return (
    <div className="pp-photo" data-photo-id={photo.id}>
      {/* 108x72 is too small to judge a photo by. Opening it full size is the
          only way the coordinator sees what they uploaded — sharp, the right
          way up, showing what they meant — and it is the volunteer's view. */}
      <button
        className="pp-photo-open"
        title="Poglej fotografijo v polni velikosti"
        aria-label={`Odpri fotografijo: ${photo.captionSl || `mesto ${postCode}`}`}
        onClick={onPreview}
      >
        <img
          className="pp-photo-thumb"
          src={photoUrl(photo.key)}
          alt={photo.captionSl || `Fotografija mesta ${postCode}`}
          loading="lazy"
          decoding="async"
        />
        <span className="pp-photo-zoom" aria-hidden="true">
          ⤢
        </span>
      </button>
      {readOnly ? (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ fontSize: rem(13) }}>{photo.captionSl || '—'}</div>
          {photo.captionEn ? (
            <div style={{ fontSize: rem(12.5), color: 'oklch(0.5 0.012 90)' }}>{photo.captionEn}</div>
          ) : null}
          {cover ? (
            <div className="pp-photo-meta">
              <span className="pp-photo-cover">NASLOVNA</span>
            </div>
          ) : null}
        </div>
      ) : confirming ? (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: rem(13), color: 'oklch(0.4 0.012 90)' }}>
            Izbrišem to fotografijo?
          </span>
          <div style={{ flex: 1 }} />
          <button className="pp-photo-btn pp-photo-btn--danger" onClick={onDelete}>
            Izbriši
          </button>
          <button className="pp-photo-btn" onClick={onCancelDelete}>
            Prekliči
          </button>
        </div>
      ) : (
        <>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
            <Caption
              lang="SL"
              value={photo.captionSl}
              placeholder="npr. Stoj na desni strani mostu, obrnjen proti tekačem"
              onSave={(captionSl) => onCaption({ captionSl })}
            />
            <Caption
              lang="EN"
              value={photo.captionEn}
              placeholder="the same, in English"
              onSave={(captionEn) => onCaption({ captionEn })}
            />
            <div className="pp-photo-meta">
              {cover ? <span className="pp-photo-cover">NASLOVNA</span> : null}
              <span className="pp-mono">
                {photo.width}×{photo.height} · {Math.round(photo.byteSize / 1024)} kB
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <button
              className="pp-photo-btn pp-photo-btn--icon"
              title="Premakni naprej"
              aria-label="Premakni naprej"
              disabled={first}
              onClick={() => onMove(-1)}
            >
              ↑
            </button>
            <button
              className="pp-photo-btn pp-photo-btn--icon"
              title="Premakni nazaj"
              aria-label="Premakni nazaj"
              disabled={last}
              onClick={() => onMove(1)}
            >
              ↓
            </button>
            <button
              className="pp-photo-btn pp-photo-btn--icon pp-photo-btn--danger"
              title="Izbriši fotografijo"
              aria-label="Izbriši fotografijo"
              onClick={onAskDelete}
            >
              ✕
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const IDLE_MS = 4000;
const RETRY_MS = 5000;
const MAX_RETRIES = 3;

/**
 * A caption is written when the coordinator leaves the field — on blur, and on
 * unmount, because selecting another post detaches a focused input without
 * ever firing blur. A long idle timer backs both up for the case where the tab
 * is closed mid-caption.
 *
 * Not a short debounce: every save is a PATCH, an audit entry and an SSE
 * refresh for every other coordinator, so a caption typed in three bursts
 * would fill this post's history with its own drafts.
 *
 * A caption counts as saved only once the server has answered. One that
 * failed keeps its text and its red mark, and goes again: by itself after a
 * pause when the failure could pass (the network, the server), at most
 * `MAX_RETRIES` times, and otherwise on the next blur or edit. The field
 * follows the stored caption while nothing typed here is waiting.
 */
function Caption({
  lang,
  value,
  placeholder,
  onSave,
}: {
  lang: 'SL' | 'EN';
  value: string;
  placeholder: string;
  onSave: (text: string) => Promise<unknown>;
}) {
  const [text, setText] = useState(value);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<{ retry: boolean } | null>(null);
  // What the server holds as far as this field knows: the stored caption, or
  // the text its last save confirmed. The server trims, so this is trimmed.
  const saved = useRef(value);
  // The newest save still on its way, and its number: an older answer landing
  // late must not settle the field.
  const sending = useRef<string | null>(null);
  const seq = useRef(0);
  const retries = useRef(0);
  // Held in refs: the parent re-renders on every query settle, and a fresh
  // callback in a dependency list would restart the timer each time. `latest`
  // is what the unmount cleanup reads, since it cannot see the render's state.
  const save = useRef(onSave);
  save.current = onSave;
  const latest = useRef(text);
  latest.current = text;

  const [base, setBase] = useState(value);
  if (base !== value) {
    setBase(value);
    if (text === base || text.trim() === value) setText(value);
  }
  useEffect(() => {
    if (sending.current === null) saved.current = value;
  }, [value]);

  const flush = () => {
    const next = latest.current.trim();
    if (next === (sending.current ?? saved.current)) return;
    const mine = (seq.current += 1);
    sending.current = next;
    setSaving(true);
    save.current(next).then(
      () => {
        saved.current = next;
        if (seq.current !== mine) return;
        sending.current = null;
        retries.current = 0;
        setSaving(false);
        setFailure(null);
      },
      (err: unknown) => {
        if (seq.current !== mine) return;
        sending.current = null;
        setSaving(false);
        setFailure({ retry: isTransient(err) && retries.current < MAX_RETRIES });
      },
    );
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;

  // Safety net for a tab closed mid-caption, and the retry after a failure
  // that could pass; blur and unmount do the real work.
  useEffect(() => {
    if (saving || text.trim() === saved.current) return;
    if (failure && !failure.retry) return;
    const timer = setTimeout(
      () => {
        if (failure) retries.current += 1;
        flushRef.current();
      },
      failure ? RETRY_MS : IDLE_MS,
    );
    return () => clearTimeout(timer);
  }, [text, saving, failure]);

  // Selecting another post remounts the editor. A focused input removed from
  // the DOM never fires blur, so without this the last caption typed would be
  // dropped on the floor — while the card promises it saves immediately.
  useEffect(() => () => flushRef.current(), []);

  return (
    <label className="pp-photo-cap">
      <span className="pp-mono">{lang}</span>
      <input
        className="pp-photo-caption"
        value={text}
        maxLength={PHOTO_CAPTION_MAX}
        placeholder={placeholder}
        aria-label={lang === 'SL' ? 'Opis fotografije (slovensko)' : 'Opis fotografije (angleško)'}
        aria-busy={saving || undefined}
        aria-invalid={failure ? true : undefined}
        title={failure ? 'Opis ni shranjen — shrani se, ko zapustiš polje.' : undefined}
        onChange={(e) => {
          setText(e.currentTarget.value);
          if (failure) {
            // An edit is a fresh attempt, on the ordinary idle timer.
            setFailure(null);
            retries.current = 0;
          }
        }}
        onBlur={flush}
      />
    </label>
  );
}

/**
 * Rows slide to their new place instead of jumping there: measure where each
 * one was, put it back with a transform, then let it travel. `key` changes
 * whenever the order does, which is the only time this has anything to do.
 */
function useFlip(ref: React.RefObject<HTMLDivElement | null>, key: string) {
  const positions = useRef(new Map<string, number>());
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    for (const row of el.querySelectorAll<HTMLElement>('[data-photo-id]')) {
      const id = row.dataset.photoId!;
      const top = row.offsetTop;
      const was = positions.current.get(id);
      positions.current.set(id, top);
      if (was === undefined || was === top || reduce) continue;
      row.style.transition = 'none';
      row.style.transform = `translateY(${was - top}px)`;
      requestAnimationFrame(() => {
        row.style.transition = 'transform 160ms var(--rr-ease-out)';
        row.style.transform = '';
      });
    }
  }, [ref, key]);
}
